import { Toast } from 'primereact/toast'
import { createContext, useContext, useRef, ReactNode } from 'react'

interface ToastMessage {
  severity: 'success' | 'info' | 'warn' | 'error'
  summary: string
  detail: string
  life?: number
}

interface ToastContextType {
  showToast: (message: ToastMessage) => void
  showSuccess: (summary: string, detail: string, life?: number) => void
  showError: (summary: string, detail: string, life?: number) => void
  showWarn: (summary: string, detail: string, life?: number) => void
  showInfo: (summary: string, detail: string, life?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const toastRef = useRef<Toast>(null)

  const showToast = (message: ToastMessage) => {
    toastRef.current?.show({
      severity: message.severity,
      summary: message.summary,
      detail: message.detail,
      life: message.life || 3000,
    })
  }

  const showSuccess = (summary: string, detail: string, life = 3000) => {
    showToast({ severity: 'success', summary, detail, life })
  }

  const showError = (summary: string, detail: string, life = 3000) => {
    showToast({ severity: 'error', summary, detail, life })
  }

  const showWarn = (summary: string, detail: string, life = 3000) => {
    showToast({ severity: 'warn', summary, detail, life })
  }

  const showInfo = (summary: string, detail: string, life = 3000) => {
    showToast({ severity: 'info', summary, detail, life })
  }

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showError, showWarn, showInfo }}
    >
      <Toast ref={toastRef} position="top-right" style={{ zIndex: 9999 }} />
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
